/**
 * Mantine theme for the Forestum SPA. Tokens and rationale: docs/frontend-design.md.
 *
 * Usage:
 *   import './theme/fonts.css';
 *   <MantineProvider theme={theme} forceColorScheme="light">
 */
import { Badge, Button, Card, createTheme, Modal, Table, Tabs, rem } from '@mantine/core';
import type { MantineColorsTuple } from '@mantine/core';

/** Brand forest scale, light to dark. Index 6 is the primary shade. */
export const forest: MantineColorsTuple = [
  '#e8f7ed', // 0
  '#c2ebcf', // 1
  '#8ad4a0', // 2
  '#52b96e', // 3
  '#3a8f55', // 4
  '#2d6a40', // 5
  '#2a6e42', // 6  primary — readable on white
  '#245233', // 7
  '#1a3d26', // 8
  '#0a1f12', // 9  hero background
];

/** Tokens that are not a Mantine colour scale. Prefer these over literals in components. */
export const brand = {
  dark:        '#243B20', // card header bars, dark buttons, client hero
  yellow:      '#EDE939', // eyebrow dots, hero CTA, accents on dark only
  bg:          '#f2f5f2', // page background, light sage
  surface:     '#ffffff',
  surface2:    '#e8efe9', // secondary surface, active tab, code chips
  border:      '#c4d4c9',
  text:        '#192b1e',
  textMuted:   '#4d6a57',
  accentBg:    '#e6f2ea', // tinted pill background
  onDark:      'rgba(255,255,255,0.85)',
  onDarkMuted: 'rgba(255,255,255,0.55)',
  onDarkLine:  'rgba(255,255,255,0.14)',
} as const;

export const shadows = {
  card:    '0 1px 4px rgba(26,43,30,0.06)',
  raised:  '0 4px 20px rgba(36,59,32,0.10)',
  accent:  '0 2px 12px rgba(42,110,66,0.20)',
  cta:     '0 4px 20px rgba(237,233,57,0.35)',
} as const;

export const theme = createTheme({
  primaryColor: 'forest',
  primaryShade: 6,
  colors: { forest },

  white: brand.surface,
  black: brand.text,

  fontFamily: "'Pangea', 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  headings: {
    fontFamily: "'Pangea', 'Plus Jakarta Sans', system-ui, sans-serif",
    fontWeight: '700',
    sizes: {
      h1: { fontSize: rem(30), lineHeight: '1.2' },
      h2: { fontSize: rem(24), lineHeight: '1.25' },
      h3: { fontSize: rem(18), lineHeight: '1.3' },
      h4: { fontSize: rem(16), lineHeight: '1.4' },
    },
  },
  fontSizes: {
    xs: rem(12),
    sm: rem(14),
    md: rem(15),
    lg: rem(18),
    xl: rem(24),
  },

  defaultRadius: 'md',
  radius: {
    xs: rem(4),
    sm: rem(8),   // chips, small controls
    md: rem(10),  // buttons, inputs
    lg: rem(12),  // cards
    xl: rem(16),  // feature cards, modals
  },

  shadows: {
    xs: shadows.card,
    sm: shadows.card,
    md: shadows.raised,
    lg: shadows.raised,
    xl: '0 8px 32px rgba(36,59,32,0.16)',
  },

  other: { brand, shadows },

  components: {
    Card: Card.extend({
      defaultProps: { withBorder: true, radius: 'lg', padding: 'lg', shadow: 'xs' },
      styles: { root: { borderColor: brand.border, backgroundColor: brand.surface } },
    }),
    Button: Button.extend({
      defaultProps: { radius: 'md', fw: 600 },
    }),
    Badge: Badge.extend({
      defaultProps: { radius: 'xl', variant: 'light', fw: 600, tt: 'none' },
    }),
    Tabs: Tabs.extend({
      defaultProps: { variant: 'pills', radius: 'sm' },
    }),
    Table: Table.extend({
      defaultProps: { highlightOnHover: true, highlightOnHoverColor: 'forest.0', verticalSpacing: 'sm', horizontalSpacing: 'md' },
      styles: {
        th: {
          fontSize: rem(12),
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: brand.textMuted,
          borderBottomColor: brand.border,
        },
        td: { borderBottomColor: brand.border },
      },
    }),
    Modal: Modal.extend({
      defaultProps: { radius: 'xl', centered: true, overlayProps: { backgroundOpacity: 0.45 } },
    }),
  },
});
